import { Camera, Check, FileText, RefreshCw, Send } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { BrowserFrame, PhoneFrame } from "@/components/blocks/frames/Frames";
import { DURATION, EASE, SPRING } from "@/components/motion/motion";
import { useSceneLoop } from "@/hooks/use-scene-loop";
import { useI18n } from "@/lib/i18n/use-i18n";
import { cn } from "@/lib/utils";

/** 0 filling the survey, 1 photo added, 2 sent and visible at the office. */
const FIELD_DURATIONS = [1900, 1500, 4200] as const;

/**
 * Field survey typed on a phone, then available at the office without
 * re-entry: the row updates and the report is ready.
 */
export function FieldReportScene({ className }: { className?: string }) {
	const { dict } = useI18n();
	const field = dict.scenes.field;
	const report = dict.scenes.assemble.report;
	const tag = dict.common.exampleBadge;
	const { ref, index } = useSceneLoop(FIELD_DURATIONS, { initial: 2 });
	const sent = index === 2;
	const photos = index === 0 ? 2 : 3;

	return (
		<div
			ref={ref}
			className={cn(
				"relative aspect-[1/1.05] w-full sm:aspect-[5/4]",
				className,
			)}
		>
			<BrowserFrame
				host={dict.scenes.host}
				tag={tag}
				className="absolute top-0 right-0 w-[82%]"
				contentClassName="p-4 sm:p-5"
			>
				<div className="mb-4 flex items-center justify-between">
					<p className="text-sm font-semibold">{field.dashTitle}</p>
					<span
						className={cn(
							"flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-medium transition-colors duration-500",
							sent && "border-signal/50 bg-signal/10",
						)}
					>
						<RefreshCw
							className={cn(
								"size-3 transition-transform duration-700",
								sent && "rotate-180 text-signal-ink",
							)}
						/>
						{field.sync}
					</span>
				</div>
				<ul className="space-y-2 pl-[22%] text-[11px] sm:pl-[26%]">
					{Object.entries(field.rows).map(([key, row], rowIndex) => {
						const highlighted = rowIndex === 0 && sent;
						const status =
							rowIndex === 0 && !sent ? field.rows.c.status : row.status;
						return (
							<li
								key={key}
								className="relative flex items-center gap-3 overflow-hidden rounded-lg border bg-background px-3 py-2.5"
							>
								<motion.span
									className="absolute inset-0 origin-left bg-signal/10"
									initial={false}
									animate={{ scaleX: highlighted ? 1 : 0 }}
									transition={{
										duration: 0.7,
										ease: EASE,
										delay: highlighted ? 0.3 : 0,
									}}
								/>
								<span className="relative min-w-0 flex-1 truncate font-medium">
									{row.site}
								</span>
								<span
									className={cn(
										"relative shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium",
										highlighted
											? "bg-signal text-signal-foreground"
											: "bg-foreground/[0.06] text-muted-foreground",
									)}
								>
									{status}
								</span>
							</li>
						);
					})}
				</ul>
				<motion.div
					className="mt-3 ml-[22%] flex items-center gap-3 rounded-lg border bg-background px-3 py-2.5 sm:ml-[26%]"
					initial={false}
					animate={{ opacity: sent ? 1 : 0.35 }}
					transition={{ duration: 0.5, ease: EASE, delay: sent ? 0.8 : 0 }}
				>
					<FileText className="size-4 shrink-0 text-signal-ink" />
					<span className="min-w-0 flex-1 truncate text-[11px] font-medium">
						{report.title}
					</span>
					<span className="font-mono text-[9.5px] text-muted-foreground">
						{sent ? report.status : null}
					</span>
				</motion.div>
			</BrowserFrame>

			<PhoneFrame className="absolute bottom-0 left-0 w-[44%] max-w-[13.5rem] sm:w-[40%] rounded-[2rem] p-[5px] [&>div]:rounded-[1.7rem]">
				<div className="flex h-full flex-col gap-2.5 px-3 pt-1 pb-3">
					<p className="text-[11px] font-semibold">{field.formTitle}</p>
					<div className="rounded-lg border px-2.5 py-1.5">
						<p className="text-[8.5px] text-muted-foreground">
							{field.pressure}
						</p>
						<p className="font-mono text-[11px] font-medium">
							{field.pressureValue}
							{index === 0 ? (
								<span className="caret ml-px text-signal">|</span>
							) : null}
						</p>
					</div>
					<div className="rounded-lg border px-2.5 py-1.5">
						<p className="text-[8.5px] text-muted-foreground">{field.state}</p>
						<p className="mt-0.5 inline-flex rounded-md bg-foreground px-1.5 py-0.5 text-[9.5px] font-medium text-background">
							{field.stateValue}
						</p>
					</div>
					<div>
						<p className="mb-1 text-[8.5px] text-muted-foreground">
							{field.photos}
						</p>
						<div className="grid grid-cols-3 gap-1.5">
							{[0, 1, 2].map((photo) => (
								<motion.span
									key={photo}
									className="dot-grid flex aspect-square items-center justify-center rounded-md bg-muted"
									animate={{
										opacity: photo < photos ? 1 : 0.35,
										scale: photo < photos ? 1 : 0.9,
									}}
									transition={SPRING}
								>
									{photo >= photos ? (
										<Camera className="size-3 text-muted-foreground" />
									) : (
										<span className="size-2 rounded-full bg-signal/60" />
									)}
								</motion.span>
							))}
						</div>
					</div>
					<div className="mt-auto">
						<AnimatePresence mode="wait" initial={false}>
							<motion.span
								key={sent ? "sent" : "send"}
								initial={{ opacity: 0, y: 6 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0, y: -6 }}
								transition={{ duration: DURATION.state, ease: EASE }}
								className={cn(
									"flex h-8 items-center justify-center gap-1.5 rounded-lg text-[10px] font-semibold",
									sent
										? "bg-signal text-signal-foreground"
										: "bg-foreground text-background",
								)}
							>
								{sent ? (
									<Check className="size-3" strokeWidth={3} />
								) : (
									<Send className="size-3" />
								)}
								{sent ? field.sent : field.send}
							</motion.span>
						</AnimatePresence>
					</div>
				</div>
			</PhoneFrame>
		</div>
	);
}
